import { useCharacteristics, useCostBreakdown, useNodeById } from '@/stores/analysisStore';
import { analysisActions } from '@/stores/analysisStore';
import CharacteristicsForm from './characteristics/CharacteristicsForm';
import HeadlineStrip from './costBreakdown/HeadlineStrip';
import AbsoluteCosts from './costBreakdown/AbsoluteCosts';
import CostTransformations from './costBreakdown/CostTransformations';
import { buildTransformationSubtitle } from './costBreakdown/costTransformationUtils';
import CostCompositionPanel from './costBreakdown/CostCompositionPanel';
import FeedstockAttributionPanel from './costBreakdown/FeedstockAttributionPanel';
import FeedstockUtilityCosts from './costBreakdown/FeedstockUtilityCosts';
import CarbonChainSection from './carbonChain/CarbonChainSection';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePlotSync, usePlotSyncHandler } from '../providers/PlotSyncProvider';
import { getTEAYaxisLabel } from '@/utils/analysisUtils';

const CharacteristicsAndCost = () => {
  const characteristicsPlot = useCharacteristics();
  const costBreakdownPlot = useCostBreakdown();
  const { setParams } = usePlotSync();
  const node = useNodeById(costBreakdownPlot?.nodeId);
  const [showTransformation, setShowTransformation] = useState(false);

  const updatePlot = useCallback(
    async formData => {
      setParams(formData);
      await Promise.all([
        analysisActions.updatePlot('characteristics', { nodeId: formData.nodeId }),
        analysisActions.updatePlot('tea_breakdown', formData),
        // independent of the levelization above: the chain is always priced by
        // the CO2 itself
        analysisActions.loadCarbonChain(formData.nodeId),
      ]);
    },
    [setParams],
  );

  const defaultValues = useMemo(() => {
    return {
      nodeId: costBreakdownPlot?.nodeId,
      levelizeBy: costBreakdownPlot?.levelizeBy,
      levelizeUnit: costBreakdownPlot?.levelizeUnit,
      levelizableIos: costBreakdownPlot?.levelizable_ios,
    };
  }, [costBreakdownPlot]);

  const { syncData } = usePlotSyncHandler(updatePlot, defaultValues);

  useEffect(() => {
    syncData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const topLevelCostRows = costBreakdownPlot?.results?.[0]?.rows;
  // The top-level feedstock category is relabeled by the backend to
  // "Cost of incoming {stream(s)}" (see _incoming_feedstock_label). Reuse that
  // single source to name the streams in the attribution panel title.
  const INCOMING_PREFIX = 'Cost of incoming ';
  const feedstockStreams = topLevelCostRows
    ?.find(row => row.label?.startsWith(INCOMING_PREFIX))
    ?.label?.slice(INCOMING_PREFIX.length);
  const levelizedCost =
    costBreakdownPlot?.levelized_cost ?? costBreakdownPlot?.results?.[0]?.total?.toNumber?.() ?? null;
  const sellingPrice = costBreakdownPlot?.selling_price ?? null;
  const lcohUnit = node && costBreakdownPlot?.levelizeUnit ? getTEAYaxisLabel(costBreakdownPlot, node, false) : '';

  const totalCosts = characteristicsPlot?.results?.find(s => s.header === 'Total asset costs')?.rows;
  const facilityCharacteristics = characteristicsPlot?.results?.find(s => s.header === 'Asset characteristics')?.rows;
  const costTransformations = costBreakdownPlot?.cost_transformations ?? null;
  const transformationDetailsAvailable = costTransformations != null && (costTransformations.rows?.length ?? 0) > 0;

  if (!defaultValues.nodeId) {
    return (
      <div className="relative">
        <span className="loading loading-dots loading-md"></span>
      </div>
    );
  }

  const hasFeedstock = (costBreakdownPlot?.feedstock_costs?.length ?? 0) > 0;
  const hasFeedstockUtilityBreakdown = (costBreakdownPlot?.feedstock_utility_breakdown?.rows?.length ?? 0) > 0;
  // The feedstock/utility pricing table and the transformation table are both
  // supplementary detail, so a single toggle reveals both. Guard against the
  // edge case where only one of the two data sets is present.
  const detailsAvailable = transformationDetailsAvailable || hasFeedstockUtilityBreakdown;
  const showingDetails = showTransformation && detailsAvailable;
  const showingTransformationTable = showingDetails && transformationDetailsAvailable;
  const analysisStartYear = costTransformations?.analysis_start_year;
  const summarySubtitle = analysisStartYear
    ? `Absolute values in ${analysisStartYear} dollars, undiscounted`
    : 'Absolute values, undiscounted';
  const subtitle = showingTransformationTable ? buildTransformationSubtitle(costTransformations) : summarySubtitle;

  return (
    <div className="relative flex flex-col gap-2">
      <CharacteristicsForm key={defaultValues.nodeId} onSubmit={updatePlot} defaultValues={defaultValues} />

      <HeadlineStrip
        levelizedCost={levelizedCost}
        sellingPrice={sellingPrice}
        lcohUnit={lcohUnit}
        nodeLabel={node?.label}
        facilityCharacteristics={facilityCharacteristics}
      />

      <section className="py-4 border-b border-gray-200 flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Total asset costs</h3>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
          {detailsAvailable && (
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-900 whitespace-nowrap"
              onClick={() => setShowTransformation(prev => !prev)}
              aria-expanded={showingDetails}
              aria-controls="cost-transformation-panel"
            >
              {showingDetails ? 'Hide transformation details' : 'Show transformation details →'}
            </button>
          )}
        </div>
        <div
          id="cost-transformation-panel"
          key={showingTransformationTable ? 'detailed' : 'summary'}
          className="animate-fade-in"
        >
          {showingTransformationTable ? (
            <CostTransformations transformations={costTransformations} />
          ) : (
            <AbsoluteCosts totalCosts={totalCosts} />
          )}
        </div>
      </section>

      {showingDetails && (
        <div className="animate-fade-in">
          <FeedstockUtilityCosts breakdown={costBreakdownPlot?.feedstock_utility_breakdown} />
        </div>
      )}

      <section className={`py-4 grid grid-cols-1 ${hasFeedstock ? 'lg:grid-cols-2' : ''} gap-x-10 gap-y-6`}>
        <CostCompositionPanel rows={topLevelCostRows} unit={lcohUnit} />
        <FeedstockAttributionPanel
          feedstockCosts={costBreakdownPlot?.feedstock_costs}
          unit={lcohUnit}
          streams={feedstockStreams}
        />
      </section>

      <CarbonChainSection />
    </div>
  );
};

export default CharacteristicsAndCost;
